/**
 * `compliance-api` is FastAPI/Pydantic, so every contract on the wire is snake_case
 * (`run_id`, `cited_paths`, `agent_session_id`). The UI is camelCase throughout. These
 * two functions are the only place that gap is bridged — `services/api` applies them to
 * every request body and response, so no feature has to think about it.
 */

type Primitive = string | number | boolean | null | undefined;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];
export type JsonValue = Primitive | JsonObject | JsonArray;

const snakeToCamel = (str: string): string => str.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

const camelToSnake = (str: string): string => str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

const convertKeys = <T extends JsonValue>(obj: T, convert: (key: string) => string): T => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeys(item, convert)) as T;
  }
  /** `typeof null === 'object'`, and a `Date`/`File` must be passed through untouched. */
  if (obj && typeof obj === 'object' && Object.getPrototypeOf(obj) === Object.prototype) {
    return Object.keys(obj).reduce((acc, key) => {
      acc[convert(key)] = convertKeys((obj as JsonObject)[key], convert);
      return acc;
    }, {} as JsonObject) as T;
  }
  return obj;
};

export const convertKeysToCamel = <T extends JsonValue>(obj: T): T => convertKeys(obj, snakeToCamel);

export const convertKeysToSnake = <T extends JsonValue>(obj: T): T => convertKeys(obj, camelToSnake);
